import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, X, Sparkles, ArrowRightLeft } from "lucide-react";
import SlideToolbar from "@/components/studio/SlideToolbar";
import SlideRail from "@/components/studio/SlideRail";
import { SlideMedia, marker } from "@/components/studio/SlideMedia";
import PresentMode from "@/components/studio/PresentMode";
import { animFor, transFor } from "@/lib/slideMotion";

export default function SlideEditorCore({ doc, setDoc, config, pageRef }) {
  const active = Math.min(doc.activeSlide || 0, doc.slides.length - 1);
  const slide = doc.slides[active];
  const [menu, setMenu] = useState(null);
  const [playKey, setPlayKey] = useState(0);
  const [presenting, setPresenting] = useState(false);
  const markPresented = () => { if (!doc.presented) setDoc({ ...doc, presented: true }); };

  const theme = config.themes.find((t) => t.id === slide.theme) || config.themes[0];
  const bg = (config.backgrounds || []).find((b) => b.id === slide.background);
  const canvasBg = bg ? bg.color : theme.bg;
  const fg = bg ? bg.fg : theme.fg;

  const updateSlide = (patch) => setDoc({ ...doc, slides: doc.slides.map((s, i) => (i === active ? { ...s, ...patch } : s)) });
  const bullets = slide.bullets || [];
  const setBullet = (i, val) => { const b = [...bullets]; b[i] = val; updateSlide({ bullets: b }); };
  const addBullet = () => updateSlide({ bullets: [...bullets, ""] });
  const removeBullet = (i) => updateSlide({ bullets: bullets.filter((_, idx) => idx !== i) });
  const anim = animFor(slide.animation);
  const trans = transFor(slide.transition);
  const hasMedia = slide.image || slide.chart || slide.table || slide.video;
  const style = slide.bulletStyle || "dot";

  const titleInput = (cls, placeholder, color) => (
    <input data-testid="slide-title-input" value={slide.title} onChange={(e) => updateSlide({ title: e.target.value })} placeholder={placeholder}
      className={`w-full bg-transparent outline-none font-bold placeholder:opacity-40 ${cls}`} style={{ color }} />
  );

  const bulletList = () => (
    <div className="space-y-1.5 min-w-0">
      {bullets.map((b, i) => (
        <div key={i} className="flex items-center gap-2 group">
          <span data-testid={`slide-bullet-marker-${i}`} className="w-5 text-right shrink-0 tabular-nums" style={{ color: theme.accent }}>{marker(style, i)}</span>
          <input data-testid={`slide-bullet-${i}`} value={b} onChange={(e) => setBullet(i, e.target.value)} placeholder={style === "number" ? "Step" : "Bullet point"}
            className="flex-1 bg-transparent outline-none placeholder:opacity-40 min-w-0" style={{ color: fg }} />
          <button data-testid={`slide-bullet-remove-${i}`} onClick={() => removeBullet(i)} className="opacity-0 group-hover:opacity-100 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      <button data-testid="slide-bullet-add" onClick={addBullet} className="text-sm inline-flex items-center gap-1 opacity-60 hover:opacity-100"><Plus className="w-3.5 h-3.5" /> Add {style === "number" ? "step" : "bullet"}</button>
    </div>
  );

  const renderLayout = () => {
    if (slide.layout === "title") return (
      <div className="m-auto text-center w-full">
        {titleInput("text-3xl sm:text-4xl text-center", "Presentation Title", fg)}
        <input data-testid="slide-bullet-0" value={bullets[0] || ""} onChange={(e) => setBullet(0, e.target.value)} placeholder="Subtitle / your name"
          className="w-full bg-transparent outline-none text-lg text-center mt-3 placeholder:opacity-40" style={{ color: theme.accent }} />
      </div>
    );
    if (slide.layout === "section") return (
      <div className="my-auto w-full pl-4 border-l-4" style={{ borderColor: theme.accent }} data-testid="slide-section-layout">
        <p className="text-xs uppercase tracking-widest opacity-60 mb-1">Section</p>
        {titleInput("text-3xl sm:text-4xl", "Section title", fg)}
        <input data-testid="slide-bullet-0" value={bullets[0] || ""} onChange={(e) => setBullet(0, e.target.value)} placeholder="One line about this section"
          className="w-full bg-transparent outline-none text-base mt-2 placeholder:opacity-40" style={{ color: theme.accent }} />
      </div>
    );
    if (slide.layout === "blank") return (
      <div className="m-auto w-full text-center" data-testid="slide-blank-layout">
        {titleInput("text-2xl text-center", "Blank slide — pick a layout to add content", fg)}
      </div>
    );
    return (
      <>
        {titleInput("text-2xl sm:text-3xl mb-4", "Slide title", theme.accent)}
        <div className={`flex-1 grid gap-4 min-h-0 ${slide.layout === "two-content" || hasMedia ? "sm:grid-cols-2" : "grid-cols-1"}`}>
          {slide.layout === "title-body" ? (
            <textarea data-testid="slide-body-input" value={slide.body || ""} onChange={(e) => updateSlide({ body: e.target.value })} placeholder="Write a short paragraph — 1 to 3 sentences that explain this slide."
              className="w-full h-full min-h-[96px] bg-transparent outline-none resize-none text-base leading-relaxed placeholder:opacity-40" style={{ color: fg }} />
          ) : bulletList()}
          {hasMedia && <SlideMedia slide={slide} theme={theme} fg={fg} updateSlide={updateSlide} />}
          {!hasMedia && slide.layout === "two-content" && <div data-testid="slide-second-column" className="rounded-lg border border-dashed p-3 text-xs opacity-60 flex items-center justify-center" style={{ borderColor: theme.accent }}>Second column — insert an image, chart, table or video</div>}
        </div>
      </>
    );
  };

  return (
    <div className="rounded-2xl overflow-hidden border border-white/10">
      <SlideToolbar doc={doc} setDoc={setDoc} slide={slide} config={config} updateSlide={updateSlide} menu={menu} setMenu={setMenu} onPlay={() => setPlayKey((k) => k + 1)} onPresent={() => { setMenu(null); setPresenting(true); }} />
      {presenting && <PresentMode doc={doc} config={config} startAt={active} onClose={() => setPresenting(false)} onReachedEnd={markPresented} />}
      <div className="bg-slate-200 p-3 sm:p-5 flex flex-col lg:flex-row gap-4 max-h-[600px] overflow-auto hq-scrollbar" onClick={() => setMenu(null)}>
        <SlideRail doc={doc} setDoc={setDoc} config={config} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap min-h-[20px]">
            {slide.animation !== "none" && <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-primary/15 text-primary"><Sparkles className="w-3 h-3" /> {(config.animations.find((a) => a.id === slide.animation) || {}).name}</span>}
            {slide.transition !== "none" && <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-[#22D3EE]/15 text-[#0e7490]"><ArrowRightLeft className="w-3 h-3" /> {(config.transitions.find((t) => t.id === slide.transition) || {}).name} transition</span>}
            {bg && <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-slate-300 text-slate-700" data-testid="slide-bg-badge">Background: {bg.name}</span>}
          </div>
          <div className="rounded-lg overflow-hidden shadow-2xl" style={{ perspective: 1200 }}>
            <motion.div key={`t${playKey}`} initial={trans.initial} animate={trans.animate} data-testid="slide-transition-layer">
              <div ref={pageRef} data-testid="slide-canvas" className="aspect-video p-6 sm:p-8 flex flex-col" style={{ background: canvasBg, color: fg }}>
                <motion.div key={`a${playKey}`} variants={anim} initial="hidden" animate="show" data-testid="slide-anim-layer" className="flex-1 flex flex-col min-h-0">
                  {renderLayout()}
                </motion.div>
              </div>
            </motion.div>
          </div>
          <button data-testid="slide-preview-hint" onClick={() => setPlayKey((k) => k + 1)}
            className="mt-2 text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Replay preview of this slide's transition &amp; animation
          </button>
          <div className="mt-3 bg-white rounded-lg border border-slate-300 p-3">
            <p className="text-xs font-medium text-slate-500 mb-1">Speaker notes (what you'll say — audience can't see these)</p>
            <textarea data-testid="slide-notes-input" value={slide.notes} onChange={(e) => updateSlide({ notes: e.target.value })} rows={2}
              placeholder="Type the words you'd say when presenting this slide…"
              className="w-full bg-transparent outline-none resize-none text-sm text-slate-800 placeholder:text-slate-400" />
          </div>
        </div>
      </div>
    </div>
  );
}
